import { router } from "@inertiajs/react";
import { useState } from "react";

import ConfirmDialog from "@/components/ConfirmDialog";
import type { Column, PagyMeta, RowAction } from "@/components/DataTable";
import DataTable from "@/components/DataTable";
import FilterBar, { type FilterField } from "@/components/FilterBar";
import LinkButton from "@/components/LinkButton";
import { usePermissions } from "@/lib/permissions";
import { centers } from "@/routes";
import type { Center } from "@/types";

function exportUrl(path: string, filters: Record<string, string>): string {
  // Keep blank values: an explicit empty param (e.g. "status=") tells the
  // backend NOT to apply that filter's default.
  const qs = new URLSearchParams(Object.entries(filters)).toString();
  return qs ? `${path}?${qs}` : path;
}

const STATUS_LABELS: Record<string, string> = {
  active: "Activo",
  inactive: "Inactivo",
};

type Props = {
  data: Center[];
  pagy: PagyMeta;
  filters: Record<string, string>;
};

const COLUMNS: Column<Center>[] = [
  {
    header: "Nombre",
    cell: (c) => c.name || "-",
  },
  {
    header: "Estado",
    cell: (c) => (c.status ? (STATUS_LABELS[c.status] ?? c.status) : "-"),
  },
  {
    header: "Director/a",
    cell: (c) => c.director_name || "-",
  },
  {
    header: "Email",
    cell: (c) => c.director_email || "-",
  },
  {
    header: "Encargado/a Plataforma",
    cell: (c) => c.mandated_name || "-",
  },
  {
    header: "Email",
    cell: (c) => c.mandated_email || "-",
  },
];

const FILTER_FIELDS: FilterField[] = [
  {
    key: "name",
    label: "Nombre",
    type: "text",
    placeholder: "Buscar por nombre...",
  },
  {
    key: "status",
    label: "Estado",
    type: "select",
    options: [
      { value: "", label: "Todos" },
      { value: "active", label: "Activo" },
      { value: "inactive", label: "Inactivo" },
    ],
  },
];

export default function CentersIndex({ data: rows, pagy, filters }: Props) {
  const permissions = usePermissions();

  const [deleteTarget, setDeleteTarget] = useState<Center | null>(null);

  const actions: RowAction<Center>[] = [
    ...(permissions.canManageAdminOnlyRecords
      ? ([
          {
            label: "Editar",
            href: (c) => centers.edit(c.id).url,
            variant: "primary",
          },
        ] satisfies RowAction<Center>[])
      : []),
    ...(permissions.canManageAdminOnlyRecords
      ? ([
          {
            label: "Eliminar",
            onClick: (c) => setDeleteTarget(c),
            variant: "danger",
          },
        ] satisfies RowAction<Center>[])
      : []),
  ];

  function navigate(
    params: Record<string, string | number>,
    opts?: { replace?: boolean },
  ) {
    router.get(centers.index().url, params, {
      preserveScroll: true,
      replace: opts?.replace ?? false,
    });
  }

  return (
    <>
      <div className="w-full py-10">
        <div className="px-6 sm:px-8 lg:px-12">
          <div className="mb-6 flex items-center justify-between gap-4">
            <h1 className="font-sans text-3xl font-semibold text-ink">
              Centros
            </h1>

            {permissions.canManageAdminOnlyRecords && (
              <LinkButton
                href={centers.new().url}
                variant="primary"
                className="px-4 py-1.5 text-sm"
              >
                Nuevo
              </LinkButton>
            )}
          </div>

          <FilterBar
            fields={FILTER_FIELDS}
            values={filters}
            onChange={(newFilters) =>
              navigate({ ...newFilters, page: 1 }, { replace: true })
            }
          />
        </div>

        <DataTable
          columns={COLUMNS}
          rows={rows}
          keyExtractor={(c) => c.id}
          actions={actions}
          utilities={
            <a
              href={exportUrl("/centers/export_excel", filters)}
              className="font-sans text-sm font-light text-ink underline underline-offset-2 hover:text-steel"
            >
              Exportar Excel
            </a>
          }
          pagy={pagy}
          onPageChange={(page) => navigate({ ...filters, page })}
          emptyMessage="No hay centros registrados."
        />
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Eliminar centro"
        message={`Se eliminará el centro ${deleteTarget?.name ?? ""}.`}
        confirmLabel="Eliminar"
        variant="danger"
        onConfirm={() => {
          if (deleteTarget) {
            router.delete(centers.destroy(deleteTarget.id).url, {
              preserveScroll: true,
            });
          }
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
